import useSWR, { SWRConfiguration } from 'swr'

import { UsageResponse, UsageTime } from '@infura/packages.api'
import { isAxiosError } from '@infura/packages.common/errors'

import { InfuraUsage } from 'components/billing/usage/usage-types'

import { api } from 'lib/api/api'

type TotalUsagePerMethodKey = [InfuraUsage.UsageQuery, InfuraUsage.UsagePeriodValues[number], UsageTime['intervals']]

const defaultUsageFetcherConfig: SWRConfiguration = {
  errorRetryCount: 1,
  errorRetryInterval: 60000,
  focusThrottleInterval: 30000,
  loadingTimeout: 30000
}

const getUsage = async ([query, period, intervals]: TotalUsagePerMethodKey) => {
  try {
    const response = await api.getTotalUsage({
      datapoint: 'count',
      query: query,
      time: intervals
        ? {
            intervals
          }
        : {
            from: period,
            tick: period === '24h' ? 'h' : 'd'
          },
      userTimestamp: new Date().toISOString().split('.')[0] + 'Z'
    })

    return response.data
  } catch (e: any) {
    if (isAxiosError(e)) {
      throw e?.response?.data
    }
    throw e
  }
}

export const useUsage = ({
  intervals,
  method,
  period,
  refreshInterval = 0
}: {
  intervals?: UsageTime['intervals']
  method: InfuraUsage.UsageQuery
  period?: InfuraUsage.UsagePeriodValues[number]
  refreshInterval?: number
}) => {
  const swrConfig = {
    ...defaultUsageFetcherConfig,
    fetcher: getUsage,
    refreshInterval,
    refreshWhenHidden: false, //  don't refresh when the page is not visible
    refreshWhenOffline: false
  }

  const swrKey = [method, period, intervals]

  const { data: totalUsageData, ...rest } = useSWR(swrKey, swrConfig)

  return {
    totalUsageData: totalUsageData as UsageResponse,
    ...rest
  }
}
